import type { DialogueTree } from './types';

/**
 * Senator. Tone: practiced, never speaks his own name in writing, every
 * sentence is plausibly deniable. Cash flows toward him. Influence flows from him.
 */
export const SENATOR_TREE: DialogueTree = {
  contactId: 'senator',
  rootChoices: [
    {
      id: 'donation',
      label: 'donate to the campaign',
      playerSays: "donating to the campaign",
      responses: [
        {
          text: "appreciated. always.\n\nthe pac structure has been simplified. you can give in three places. for legal reasons, give in all three. don\'t ask which is which. that\'s a feature.",
          followUps: [
            { id: 'donation-small', label: "\$5K",
              playerSays: "wiring \$5K",
              effects: [{ type: 'cash', value: -5000 }, { type: 'tribeRep', value: 4, target: 'maga' }, { type: 'note', value: '-$5K to senator pac. +4 maga rep' }],
              responses: [
                { text: "received. the candidate is, between us, aware. he is not aware. but he is aware. you understand." },
              ],
            },
            { id: 'donation-big', label: "\$50K — bundled.",
              playerSays: "\$50K. bundled. through three vehicles.",
              effects: [{ type: 'cash', value: -50000 }, { type: 'tribeRep', value: 12, target: 'maga' }, { type: 'fbiHeat', value: 4 }, { type: 'note', value: '-$50K bundled. +12 maga rep. +4 FBI heat — bundling is technically legal, theoretically' }],
              responses: [
                { text: "the candidate, in due course, will know your name without being told it.\n\nthat is the value proposition." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'ask-favor',
      label: 'ask for a favor',
      playerSays: "i need a favor in DC",
      responses: [
        {
          text: "the candidate does not do favors. the candidate does \"alignments of interest.\" tell me what you need. i\'ll translate.",
          followUps: [
            { id: 'favor-tariff', label: "soften a tariff that\'s killing my position",
              playerSays: "soften a tariff that\'s killing my position",
              effects: [{ type: 'cash', value: -25000 }, { type: 'fbiHeat', value: 6 }, { type: 'note', value: 'paid $25K for a tariff carve-out request. fbi heat +6.' }],
              responses: [
                { text: "the relevant subcommittee will consider it. consideration is the value. the outcome is a separate consideration.\n\n\$25K, we will say, for the consideration." },
              ],
            },
            { id: 'favor-leak', label: "leak a fed nominee name to me before press",
              playerSays: "leak a fed nominee to me before press",
              effects: [{ type: 'fbiHeat', value: 12 }, { type: 'secHeat', value: 8 }, { type: 'cash', value: -100_000 }, { type: 'note', value: 'paid $100K for a fed leak. heat +12 fbi / +8 sec. you will hear about this.' }],
              responses: [
                { text: "the candidate, hypothetically, has a friend at the white house.\n\nthe friend will text you a name at 5:47am. before treasury sees it. you will trade on it. you will not trade on it visibly.\n\n\$100K. we will not put this in writing." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'distance',
      label: 'distance yourself from the senator',
      playerSays: "i think we should cool it for a while",
      effects: [{ type: 'tribeRep', target: 'maga', value: -8 }, { type: 'fbiHeat', value: -5 }, { type: 'note', value: 'cooled with senator. -8 maga rep. -5 fbi heat.' }],
      responses: [
        {
          text: "the candidate values your discretion. and your absence. they are, frequently, the same thing.\n\nthe channel remains open. it always remains open.",
        },
      ],
    },

    {
      id: 'rage',
      label: 'rage at the senator',
      destructive: true,
      playerSays: "you used me. you absolutely used me.",
      effects: [{ type: 'tribeRep', target: 'maga', value: -10 }, { type: 'fbiHeat', value: -3 }, { type: 'note', value: 'raged at senator. burned the bridge. -10 maga rep.' }],
      responses: [
        {
          text: "i remember things. i remember everything.\n\nthe candidate also remembers. the candidate has staff to remember.\n\nthis number will be returned to a previous configuration by friday.",
        },
      ],
    },

    {
      id: 'pitch-yourself',
      label: 'pitch yourself for a fundraiser headline',
      playerSays: "what would it take to host a fundraiser",
      responses: [
        { text: "the candidate is, in principle, available.\n\nin practice: \$100K minimum guarantee, plus you bring 14 others who also do \$25K. you organize. we attend. we leave. that\'s the deal.\n\nyou get a photo. the photo is, in 4 years, embarrassing or career-defining. we will not know which.",
          followUps: [
            { id: 'pitch-host', label: "i\'ll host. \$100K guarantee.",
              playerSays: "i\'ll host. \$100K guarantee.",
              effects: [{ type: 'cash', value: -100_000 }, { type: 'tribeRep', target: 'maga', value: 15 }, { type: 'tribeRep', target: 'press', value: -5 }, { type: 'notoriety', value: 10 }, { type: 'note', value: '-$100K fundraiser. +15 maga / -5 press / +10 notoriety' }],
              responses: [
                { text: "the date is on your calendar. you will not see it. someone in your office will see it. they will tell you the day before. that is, also, the deal." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'ask-policy',
      label: 'ask about pending policy',
      playerSays: "what\'s on the docket this session that affects markets",
      responses: [
        { text: "three things. i can\'t tell you which three. i can tell you that two will be priced in by tuesday and one will surprise everyone, including the people drafting it." },
        { text: "off the record: the appropriations bill has 47 amendments. one of them affects \$BOOM directly. nobody has read all 47. nobody will." },
      ],
    },

    {
      id: 'ask-shadow',
      label: 'ask if he can short on your behalf',
      playerSays: "can you, hypothetically, short something for me",
      destructive: true,
      effects: [{ type: 'fbiHeat', value: 8 }, { type: 'note', value: '+8 FBI heat — proposed insider trading to a sitting senator over text. that\'s now a federal record.' }],
      responses: [
        { text: "this conversation is being preserved. as a matter of policy. as a matter of law. i did not say what you think i said. you did not ask what you think you asked.\n\nwe will not be discussing this further. for the avoidance of doubt: no." },
      ],
    },
  ],
};
